import setHighestPriorityPackage from "./setHighestPriorityPackage"
import getAvailability from "./getAvailability"
import getPrice from "./getPrice"
import getWiypPrice from "./getWiypPrice"
import getLanguageFromLocale from "./getLanguageFromLocale"
import getSiteFromDomain from "./getSiteFromDomain"
import trainPackCode from "@/config/trainPackCode"
import { getAllowedOriginsForTrainPack, getTrainPackageFamily, prestationMatchesOrigin, resolveTrainPackCodeForOrigin } from "@/config/trainPackageConfig"
import { wiypActive } from '@/config/ABTestExperiments';
import { wiypCookie } from "@/config/ABTestExperimentConfig"

// Logic
import isABTestActive from "@/logic/isABTestActive"


import Cookies from "js-cookie"
// import store from "@/vue/reviews/store/store"
import { useStore } from "vuex"
const store = useStore();


const searchPrestationsForFilter = async (storeObj: any) => {
    const { state, commit, dispatch } = storeObj;
    
    if (state.productUnavailable) {
        return;
    }

    try {
        dispatch("setLoading", true);
        // Reset so the empty/"no products" state cannot flash while WIYP
        // for the newly selected package is still in flight.
        state.calledPrice = false;
        commit("setQuoteRequestsFinishedLoading", false);

        // Asynchronously fetch availability data
        const availabilityPromise = getAvailability(
            state.packCode,
            state.dates.BeginDate,
            state.dates.EndDate,
            storeObj
        );

        
        // Wait for availability data to resolve
        const availabilityResponse = await availabilityPromise;

        // Handle price requests based on AB testing conditions
        await handlePriceRequests(state, storeObj);

        dispatch("setLoading", false);

        // Check package availability and adjust if necessary
        const isPackageFromHashAvailable = availabilityResponse?.availablePackages?.some(
            (packageObj) => packageObj.codePackage === state.packCode
        );

        if (availabilityResponse && availabilityResponse.availablePackages) {
            let mostOrigins: string[] = [];
        
            if (Array.isArray(availabilityResponse.availablePackages)) {
                if (trainPackCode.includes(state.packCode)) {
                    const family = getTrainPackageFamily(state.packCode)
                    const familyOrigins = new Set<string>()
                    for (const pack of availabilityResponse.availablePackages) {
                        if (pack.codePackage && family.includes(pack.codePackage) && Array.isArray(pack.origins)) {
                            pack.origins.forEach((origin) => familyOrigins.add(origin))
                        }
                    }
                    const allowed = getAllowedOriginsForTrainPack(state.packCode)
                    mostOrigins = allowed
                        ? [...familyOrigins].filter((origin) => allowed.includes(origin))
                        : [...familyOrigins]
                } else {
                    for (const pack of availabilityResponse.availablePackages) {
                        if (Array.isArray(pack.origins) && pack.origins.length > mostOrigins.length) {
                            mostOrigins = pack.origins;
                        }
                    }
                }
            }
            commit("setFlightOrigins", mostOrigins);
        }
        

        if (availabilityResponse && !isPackageFromHashAvailable) {
            const family = getTrainPackageFamily(state.packCode)
            const originCode = state.flightOrigin?.split("-")[0] || ""
            const resolvedPack = resolveTrainPackCodeForOrigin(state.packCode, originCode)

            // URL pack + origin still match (e.g. PFTTUK + PAZ): do not swap to PFTT.
            if (resolvedPack !== state.packCode) {
                const familyMatch = availabilityResponse.availablePackages?.find(
                    (packageObj) => packageObj.codePackage === resolvedPack
                ) || availabilityResponse.availablePackages?.find(
                    (packageObj) => packageObj.codePackage && family.includes(packageObj.codePackage)
                )
                if (familyMatch?.codePackage) {
                    dispatch("setPackage", familyMatch.codePackage);
                    if (familyMatch.prestId) {
                        commit("setPackageId", familyMatch.prestId);
                    }
                } else {
                    setHighestPriorityPackage(storeObj);
                }
            }
        }
    } catch (error) {
        console.error("Error handling prestation search:", error);
        state.calledPrice = true;
        dispatch("setLoading", false); // Ensure loading is set to false on error
    }
}


// Helper function to format dates from "MM/DD/YYYY" to "YYYY-MM-DD"
function formatDates(date: string): string {
    return date.split('/').reverse().join('-');
}

// Main function to handle price requests based on cookie settings
async function handlePriceRequests(state: any, storeObj: any) {
    
    const site = getSiteFromDomain(new URL(window.location.href).hostname);

    // We allow wiyp for HS package only if we are on cse.travelski.com
    // if ((state.packCode === "HS" && site !== 'cse.travelski.com') || !state.dates.BeginDate || !state.dates.EndDate) {
    //     console.error("cannot")
    //     return;
    // }

    if (!state.dates.BeginDate || !state.dates.EndDate) {
        state.calledPrice = true;
        return;
    }

    const beginDate = formatDates(state.dates.BeginDate);
    const endDate = formatDates(state.dates.EndDate);

    // Accommodation prestations for the current packCode (reused for other packCodes' WIYP calls)
    const packAvailabilities = state.prestMaps.seAvailabilities?.[state.packCode];
    if (!packAvailabilities) {
        state.calledPrice = true;
        return;
    }
    const prestations = Object.values(packAvailabilities);

    const sortedParticipants = state.participants.slice().sort((a: number, b: number) => b - a).join('-');
    const acceptLanguage = getLanguageFromLocale(state.language);

    // Use the getter to get an array of package/prestation pairs in proper shape
    const availablePackageCodeWiyp: { codePackage: string, prestId: string, maxPromo: number, origins?: string[] }[] = storeObj.state.availablePackageCodeWiyp || [];

    const currentPackCode = state.packCode;
    const originCode = state.flightOrigin?.split("-")[0] || "";
    const family = getTrainPackageFamily(currentPackCode);
    const resolvedPack = resolveTrainPackCodeForOrigin(currentPackCode, originCode);

    const matchesSelectedOrigin = (pack: { codePackage: string, origins?: string[] }) => {
        if (!family.includes(pack.codePackage)) return true;
        return prestationMatchesOrigin(pack, originCode);
    };

    const currentPackCalls = availablePackageCodeWiyp.filter(
        (pack) => pack.codePackage === resolvedPack && matchesSelectedOrigin(pack)
    );
    const otherPackCalls = availablePackageCodeWiyp.filter(
        (pack) => pack.codePackage !== resolvedPack && matchesSelectedOrigin(pack)
    );

    const runWiyp = (pack: { codePackage: string, prestId: string }) =>
        getWiypPrice(
            pack.codePackage,
            pack.prestId,
            prestations,
            beginDate,
            endDate,
            sortedParticipants,
            site,
            acceptLanguage,
            storeObj
        );

    // Prefetch other packages in the background (badges / later switches)
    otherPackCalls.forEach((pack) => {
        void runWiyp(pack);
    });

    // Block loading until the selected package has prices, otherwise the UI
    // briefly treats an empty filtered list as "no products found".
    if (currentPackCalls.length > 0) {
        await Promise.all(currentPackCalls.map(runWiyp));
    } else {
        state.calledPrice = true;
    }
}

export default searchPrestationsForFilter